import { Injectable, Injector } from "@angular/core";
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { AuthService, MissingRefreshTokenError, TimeoutError } from '@auth0/auth0-angular';

import { environment } from 'src/environments/environment';
import { CollaborationTokenStoreService } from "./collaboration-token-store.service";
import { CollaborationTokenService } from "./collaboration-token.service";
import { CollaborationLoggerService } from "./collaboration-logger.service";
import { CollaborationNavigationService } from "./collaboration-navigation.service";
import { GoogleAnalyticsService } from "src/app/analytics/google-analytics.service";

@Injectable({
    providedIn: "root"
})
export class CollaborationHttpHeadersInterceptor implements HttpInterceptor {

    private readonly logOptions = {
        component: 'CollaborationHttpHeadersInterceptor',
        feature: 'collaboration'
    };

    // Prevents concurrent failing requests from triggering multiple error navigations
    private isHandlingTokenError = false;

    constructor(
        private analytics: GoogleAnalyticsService,
        private tokenStore: CollaborationTokenStoreService,
        private logger: CollaborationLoggerService,
        private auth: AuthService,
        private injector: Injector,
    ) { }

    private get navigationService(): CollaborationNavigationService {
        return this.injector.get(CollaborationNavigationService);
    }

    private get tokenService(): CollaborationTokenService {
        return this.injector.get(CollaborationTokenService);
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const assessmentId = this.getAssessmentIdFromUrl() ?? this.tokenStore.getActiveAssessmentId() ?? null;

        if (!assessmentId || !req.url.startsWith(environment.api.accessPointUrl)) {
            return next.handle(req.clone({ headers: this.setCommonHeaders(req) }));
        }

        return this.auth
            .getAccessTokenSilently({
                authorizationParams: {
                    audience: environment.collaboration.auth0.audience,
                },
            })
            .pipe(
                timeout(8_000),
                catchError((error: unknown) => {
                    this.handleTokenError(error);
                    return EMPTY;
                }),
                switchMap(token => {
                    const companyId = this.tokenStore.getCompanyId();
                    const email = this.tokenStore.getCollaboratorEmail();

                    if (!token || !companyId || !email) {
                        this.handleMissingContext(!token, !companyId, !email);
                        return EMPTY;
                    }

                    const headers = this.setCommonHeaders(req)
                        .set('Authorization', `Bearer ${token}`)
                        .set('CompanyId', companyId)
                        .set('CollaboratorEmail', email);

                    return next.handle(req.clone({ headers, withCredentials: true }));
                }),
            );
    }

    private handleMissingContext(missingToken: boolean, missingCompanyId: boolean, missingEmail: boolean): void {
        const missing = [
            missingToken && 'authentication token',
            missingCompanyId && 'company ID',
            missingEmail && 'collaborator email',
        ].filter(Boolean).join(', ');

        this.logger.warn('API request blocked: missing session context', { missing }, this.logOptions);

        this.navigationService.navigateToAuthenticationErrorPage(
            new Error(`Session data is missing (${missing}). Please re-open your invitation link.`)
        );
    }

    private handleTokenError(error: unknown): void {
        if (this.isHandlingTokenError) {
            return;
        }

        this.logger.logError(error as Error, { error: (error as Error)?.message || String(error) }, this.logOptions);

        if (error instanceof TimeoutError) {
            this.isHandlingTokenError = true;
            this.navigationService.navigateToAuthenticationErrorPage(
                new Error('Authentication request timed out. Please check your connection and refresh the page, or re-open your invitation link.')
            );
            return;
        }

        if (error instanceof MissingRefreshTokenError || this.isLoginRequiredError(error)) {
            const assessmentId = this.tokenStore.getActiveAssessmentId();
            const email = this.tokenStore.getCollaboratorEmail();

            if (assessmentId && email) {
                // If there is already an active error state it means auth just failed
                // (e.g. "Invalid state" from handleRedirectCallback). In that case, do NOT
                // call loginWithRedirect — the error page is already shown and background API
                // calls from that page would otherwise create an infinite redirect loop.
                const hasActiveError = !!this.tokenStore.getCollaborationErrorState().error;
                if (hasActiveError) {
                    this.logger.info(
                        'Auth error state present, suppressing auto-redirect to prevent loop',
                        {},
                        this.logOptions,
                    );
                    return;
                }

                // Natural session expiry on an authenticated route — the guard won't fire again,
                // so re-initiate passwordless login automatically. If that attempt fails,
                // handleAuth0Callback() will navigate to the error page with a precise reason.
                this.isHandlingTokenError = true;
                this.logger.warn(
                    'Auth0 session missing on authenticated route, re-initiating passwordless login',
                    { assessmentId },
                    this.logOptions,
                );
                this.tokenService.loginWithRedirect(assessmentId, email);
            } else {
                // No session context yet — the collaboration token guard will handle the redirect to Auth0
                this.logger.info('No active Auth0 session, guard will handle redirect', {}, this.logOptions);
            }
            return;
        }

        this.isHandlingTokenError = true;
        this.navigationService.navigateToAuthenticationErrorPage(
            new Error(`Authentication failed: ${(error as Error)?.message ?? 'Unknown error'}. Please re-open your invitation link.`)
        );
    }

    private isLoginRequiredError(error: unknown): boolean {
        if (!(error instanceof Error)) return false;
        const msg = error.message?.toLowerCase() ?? '';
        return msg.includes('login required') || msg.includes('missing refresh token');
    }

    private getAssessmentIdFromUrl(): string | null {
        const url = new URL(window.location.href);

        const assessmentPageId = url.searchParams.get('assessmentPageId');
        if (assessmentPageId) return assessmentPageId;

        const match = url.pathname.match(/\/assessment-page\/([^/]+)/);
        if (match?.[1]) return match[1];

        return null;
    }

    private setCommonHeaders(req: HttpRequest<any>): HttpHeaders {
        let headers = req.headers;
        headers = headers.set('session-id', this.analytics.getSessionId());

        if (req.body instanceof Array && req.body.every(item => item.hasOwnProperty('op') && item.hasOwnProperty('path') && item.hasOwnProperty('value'))) {
            headers = headers.set('Content-Type', 'application/json-patch+json');
        }
        // Preserve a Content-Type the caller set explicitly (e.g. raw file uploads send the file's
        // own type like application/pdf); only default to JSON when none was provided.
        else if (!(req.body instanceof FormData) && !req.headers.has('Content-Type')) {
            headers = headers.set('Content-Type', 'application/json; charset=utf-8');
        }

        return headers;
    }
}
